import Swiper from 'swiper';
import { Pagination, EffectCoverflow } from 'swiper/modules';
import 'swiper/css';

class FeaturedCans extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Use requestAnimationFrame to ensure smooth initialization
    requestAnimationFrame(() => {
      this.initSwiper();
    });
  }

  initSwiper() {
    const shopAllButton = this.querySelector('.shop-all-button');
    const slides = this.querySelectorAll('.swiper-slide');
    const featuredCansEl = this;

    // Set initial URL and text from first active slide
    if (slides.length > 0) {
      const initialSlide = slides[1]; // Using 1 since initialSlide is 1
      const initialUrl = initialSlide.getAttribute('href') || '#';
      const initialDescription =
        initialSlide
          .querySelector('.featured-cans-description')
          ?.textContent.trim() || '';

      shopAllButton.setAttribute('href', initialUrl);
      shopAllButton.textContent = `Shop Now`;
    }

    const getSlideFocusableTarget = (slide) => {
      if (!slide) {
        return null;
      }

      if (slide.matches('a, button, [tabindex]')) {
        return slide;
      }

      return slide.querySelector('a, button, [tabindex]');
    };

    const getSlideDataIndex = (slide) => {
      const value = slide.getAttribute('data-swiper-slide-index');
      if (value === null) {
        return null;
      }

      const parsed = Number.parseInt(value, 10);
      return Number.isNaN(parsed) ? null : parsed;
    };

    const getOrderedSlides = (swiperInstance) => {
      const slidesList = Array.from(swiperInstance.slides).filter(
        (slide) => !slide.classList.contains('swiper-slide-duplicate')
      );
      const mapped = slidesList.map((slide, order) => ({
        slide,
        order,
        index: getSlideDataIndex(slide),
      }));
      const hasIndex = mapped.some((item) => item.index !== null);
      const sorted = mapped.slice().sort((a, b) => {
        const aKey = hasIndex ? (a.index ?? a.order) : a.order;
        const bKey = hasIndex ? (b.index ?? b.order) : b.order;
        return aKey - bKey || a.order - b.order;
      });
      const seen = new Set();
      const ordered = [];

      sorted.forEach((item) => {
        const key = hasIndex ? (item.index ?? item.order) : item.order;
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        ordered.push(item.slide);
      });

      return ordered;
    };

    const resolveSlide = (slide, orderedSlides) => {
      if (orderedSlides.includes(slide)) {
        return slide;
      }

      const dataIndex = getSlideDataIndex(slide);
      if (dataIndex === null) {
        return slide;
      }

      return (
        orderedSlides.find(
          (candidate) => getSlideDataIndex(candidate) === dataIndex
        ) || slide
      );
    };

    const setTabbableSlide = (swiperInstance, slide) => {
      const orderedSlides = getOrderedSlides(swiperInstance);
      if (orderedSlides.length === 0) {
        return slide;
      }

      const resolvedSlide = resolveSlide(slide, orderedSlides);
      orderedSlides.forEach((candidate) => {
        const target = getSlideFocusableTarget(candidate);
        if (!target) {
          return;
        }

        target.setAttribute(
          'tabindex',
          candidate === resolvedSlide ? '0' : '-1'
        );
      });

      return resolvedSlide;
    };

    const updateTabbableFromActive = (swiperInstance) => {
      if (featuredCansEl.contains(document.activeElement)) {
        return;
      }

      const orderedSlides = getOrderedSlides(swiperInstance);
      if (orderedSlides.length > 0) {
        setTabbableSlide(swiperInstance, orderedSlides[0]);
        return;
      }

      setTabbableSlide(swiperInstance, swiperInstance.slides[swiperInstance.activeIndex]);
    };

    // Calculate slidesPerView and initial slide based on viewport to match what Swiper will use
    const viewportWidth = window.innerWidth;
    let slidesPerView = 1.75;
    let initialSlideIndex = 0;

    if (viewportWidth >= 1536) {
      slidesPerView = 3.75;
    } else if (viewportWidth >= 768) {
      slidesPerView = 2.75;
    }

    // For centered slides with loop, start at 0 - Swiper handles positioning
    initialSlideIndex = 0;

    const swiper = new Swiper(this.querySelector('.swiper'), {
      modules: [Pagination, EffectCoverflow],
      centeredSlides: true,
      spaceBetween: 16,
      loop: true,
      effect: 'coverflow',
      speed: 300,
      coverflowEffect: {
        rotate: 0,
        stretch: 0,
        depth: 80,
        modifier: 1,
        scale: 0.9,
        slideShadows: false,
      },

      initialSlide: initialSlideIndex,
      watchSlidesProgress: true,
      breakpoints: {
        // Mobile view
        320: {
          slidesPerView: 1.75,
          spaceBetween: 16,
        },
        // Desktop view
        768: {
          slidesPerView: 2.75,
          spaceBetween: 40,
        },
        1536: {
          slidesPerView: 3.75,
          spaceBetween: 40,
        },
      },
      on: {
        init: function () {
          const swiperInstance = this;
          this.slides[this.activeIndex]?.classList.add('swiper-slide-active');
          updateTabbableFromActive(this);

          // Wait for Swiper to fully settle including loop mode repositioning
          // Use a combination of RAF and timeout to ensure all internal calculations complete
          const revealSlider = () => {
            swiperInstance.el.closest('featured-cans')?.classList.add('swiper-initialized');
          };

          // Triple RAF + small timeout ensures all layout and transform calculations are done
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(revealSlider, 50);
            });
          });
        },
        slideChange: function () {
          this.slides.forEach((slide) => {
            slide.classList.remove('swiper-slide-active');
          });
          this.slides[this.activeIndex]?.classList.add('swiper-slide-active');
          updateTabbableFromActive(this);

          // Update shop all button URL and text
          const activeSlide = this.slides[this.activeIndex];
          const newUrl = activeSlide?.getAttribute('href') || '#';
          const description =
            activeSlide
              ?.querySelector('.featured-cans-description')
              ?.textContent.trim() || '';

          shopAllButton.setAttribute('href', newUrl);
          shopAllButton.textContent = `Shop Now`;
        },
      },
    });

    let isRestoringFocus = false;

    const focusSlide = (slide) => {
      const resolvedSlide = setTabbableSlide(swiper, slide);
      const focusTarget = getSlideFocusableTarget(resolvedSlide);

      if (!focusTarget) {
        isRestoringFocus = false;
        return;
      }

      isRestoringFocus = true;
      focusTarget.focus({ preventScroll: true });
      setTimeout(() => {
        isRestoringFocus = false;
      }, 0);
    };

    const moveToSlide = (slide) => {
      const orderedSlides = getOrderedSlides(swiper);
      const resolvedSlide = resolveSlide(slide, orderedSlides);
      const dataIndex = getSlideDataIndex(resolvedSlide);

      if (dataIndex !== null && dataIndex !== swiper.realIndex) {
        swiper.once('slideChange', () => focusSlide(resolvedSlide));
        swiper.slideToLoop(dataIndex, 0);
        requestAnimationFrame(() => requestAnimationFrame(() => focusSlide(resolvedSlide)));
        return;
      }

      focusSlide(resolvedSlide);
    };

    const focusActiveSlide = () => {
      focusSlide(swiper.slides[swiper.activeIndex]);
    };

    const handleSlideFocus = (event) => {
      if (isRestoringFocus) {
        return;
      }

      const slide = event.currentTarget;
      if (!slide) {
        return;
      }

      moveToSlide(slide);
    };

    const handleSliderKeydown = (event) => {
      if (event.key !== 'Tab') {
        return;
      }

      const slide = event.target.closest('.swiper-slide');
      if (!slide || !featuredCansEl.contains(slide)) {
        return;
      }

      const orderedSlides = getOrderedSlides(swiper);
      if (orderedSlides.length === 0) {
        return;
      }

      const resolvedSlide = resolveSlide(slide, orderedSlides);
      const currentIndex = orderedSlides.indexOf(resolvedSlide);
      if (currentIndex < 0) {
        return;
      }

      if (event.shiftKey) {
        if (currentIndex === 0) {
          setTabbableSlide(swiper, resolvedSlide);
          return;
        }

        event.preventDefault();
        moveToSlide(orderedSlides[currentIndex - 1]);
        return;
      }

      if (currentIndex === orderedSlides.length - 1) {
        setTabbableSlide(swiper, resolvedSlide);
        return;
      }

      event.preventDefault();
      moveToSlide(orderedSlides[currentIndex + 1]);
    };

    const bindSlideFocusHandlers = () => {
      swiper.slides.forEach((slide) => {
        slide.removeEventListener('focusin', handleSlideFocus);
        slide.addEventListener('focusin', handleSlideFocus);
      });
    };

    bindSlideFocusHandlers();
    featuredCansEl.addEventListener('keydown', handleSliderKeydown);
    swiper.on('loopFix', () => {
      bindSlideFocusHandlers();
      updateTabbableFromActive(swiper);
    });

    // Handle clicks on slides - attach directly to each slide element
    // This ensures clicks work even when slides are outside wrapper bounds in loop mode
    const handleSlideClick = (e) => {
      const slide = e.currentTarget;
      if (!slide) return;

      // Prevent default only if it's an anchor tag (the slide itself)
      if (slide.tagName === 'A') {
        e.preventDefault();
      }

      // Get the href from the slide (it's an <a> tag) or from a child link
      const href = slide.getAttribute('href') || slide.querySelector('a')?.getAttribute('href');

      if (href && href !== '#') {
        // Check if it should open in new tab
        const isExternal = slide.getAttribute('target') === '_blank';
        if (isExternal) {
          window.open(href, '_blank');
        } else {
          window.location.href = href;
        }
      }
    };

    const bindSlideClickHandlers = () => {
      swiper.slides.forEach((slide) => {
        slide.removeEventListener('click', handleSlideClick);
        slide.addEventListener('click', handleSlideClick);
      });
    };

    // Bind click handlers initially and on loop fix
    bindSlideClickHandlers();
    swiper.on('loopFix', bindSlideClickHandlers);
  }
}

customElements.define('featured-cans', FeaturedCans);
